/**
 * VENTO — starts a `<deferred-media autoplay>` without waiting to be clicked.
 *
 * Horizon keeps the `<video>` inside a `<template>` and only moves it into the page when
 * `showDeferredMedia` runs, which is bound to the poster button. The `autoplay` attribute
 * the section writes onto `<deferred-media>` is read by nothing, so a video set to autoplay
 * still sat on its poster until someone tapped it. This supplies the missing trigger.
 *
 * It calls the component's own `loadContent`, so the clone, the play() call Safari needs,
 * and the "another video started" bookkeeping all stay in Horizon's hands. `focus` is
 * false on purpose: focusing a video during page load scrolls the page to it.
 *
 * A visitor who has asked for reduced motion keeps the still frame and the play button, so
 * nothing moves until they choose it.
 */

const SELECTOR = 'deferred-media[autoplay]';

/** @returns {boolean} Whether this visitor has asked for less motion. */
function prefersStillness() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @param {Element} element
 */
function start(element) {
  if (element.getAttribute('data-media-loaded')) return;
  // `loadContent` arrives with the custom element definition; without it there is nothing
  // sensible to do, and cloning the template by hand here would fight the component later.
  if (typeof (/** @type {{ loadContent?: unknown }} */ (element).loadContent) !== 'function') return;
  /** @type {{ loadContent: (focus: boolean) => void }} */ (element).loadContent(false);
}

async function run() {
  const elements = document.querySelectorAll(SELECTOR);
  if (elements.length === 0 || prefersStillness()) return;

  await customElements.whenDefined('deferred-media');

  // Waiting for the section to be on screen keeps a video far down the page off a phone's
  // data plan until it is actually about to be seen.
  if (typeof IntersectionObserver !== 'function') {
    for (const element of elements) start(element);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        start(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '200px' }
  );

  for (const element of elements) observer.observe(element);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run, { once: true });
} else {
  run();
}
